import { resolveDesktopModuleDatefieldDateFilterParameter } from 'src/stores/_infrastructure/dateTimeRangeQueryValue';

import { DateRangeSelection } from 'src/models/common';
import { GenericActivityListPagination, GenericActivityListSort } from 'src/models/genericActivityList';
import { AbstractQuery } from 'src/models/query';

export class GetTheTaskModuleBoxesForTheUserQuery extends AbstractQuery {
	name = 'IFS_DesktopModule_Task_Query_Queries_GetTheTaskModuleBoxesForTheUserQuery';
	page = 1;
	subscribeForUpdates = false;
}

export class GetTheTaskModuleTasksForTheUserQuery extends AbstractQuery {
	limit = 100;
	name = 'IFS_DesktopModule_Task_Query_Queries_GetTheTaskModuleTasksForTheUserQuery';
	page = 1;
	sort: Array<GenericActivityListSort> = [
		{
			direction: 'ASC',
			property: 'timestamp',
		},
	];
	standardFilterSelection = '';

	// `sort` kommt explizit aus `sortFor(view)` - nicht aus `pagination`. Die Pagination-Kopie des Sorts
	// hing beim ersten Fetch einer Ansicht hinter dem persistierten Wert her, Seite 1 und Seite 2 gingen
	// mit verschiedenen Sortierungen raus, der Server sortierte sie verschieden, Zeilen fehlten (Spec F8).
	constructor(
		view: string,
		date: DateRangeSelection | string,
		boxIdFilter: Array<string>,
		userIdFilter: Array<number>,
		searchTerm: string,
		hiddenSearchTerm: string,
		pagination: GenericActivityListPagination,
		sort: GenericActivityListSort,
		suppressBoxCustomCoupling = false
	) {
		super();

		this.page = pagination.page;
		this.limit = pagination.rowsPerPage;
		this.sort = [sort];

		if (view === 'open') {
			this.standardFilterSelection = 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonUnfinished';
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonUnfinished',
			});
		} else if (view === 'all') {
			this.standardFilterSelection = 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonAll';
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonAll',
			});
		} else if (view === 'mine') {
			this.standardFilterSelection = 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonMine';
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonMine',
			});
		} else if (view === 'custom') {
			this.standardFilterSelection = 'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonCustom';
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonCustom',
			});
		}

		this.parameters.push({
			DesktopModuleToolbarButtonGlobalizationFilter: true,
		});
		this.parameters.push({ DesktopModuleToolbarButtonArchivFilter: false });

		if (searchTerm !== '' && searchTerm !== undefined) {
			this.parameters.push({
				DesktopModuleToolbarTextfieldSearchField: searchTerm,
			});

			this.parameters.push({
				DesktopModuleToolbarHiddenfieldSearchField: hiddenSearchTerm,
			});

			this.parameters.push({
				DesktopModuleToolbarDatefieldDateFilter:
					'DesktopModuleToolbarSegmentedButtonTimeRangeSelectionButtonAll',
			});
		} else {
			this.parameters.push({
				DesktopModuleToolbarDatefieldDateFilter: resolveDesktopModuleDatefieldDateFilterParameter(date),
			});
		}

		// Task 3.1f: die Box→Custom-Kopplung kollabiert alle drei Ansichts-Slices (Offen/Meine/Alle) auf dieselbe
		// Custom-Query, sobald Boxen ohne User-Filter aktiv sind (Backend: last-wins auf StandardFilterSelectionButton
		// → 8/8/8). Für isolierte gespeicherte-Listen-Tabs unterdrücken die Instanz-Fetches diese Kopplung, damit jede
		// Ansicht view-scoped mit den Listen-Boxen schneidet. Default-Store (`taskList`) behält die klassische
		// Custom-Kollaps-Kopplung (byte-identisch). Backend-verifiziert für taskList: Unfinished+Boxen+"Test" → 3.
		if (!suppressBoxCustomCoupling && boxIdFilter.length > 0 && userIdFilter.length == 0) {
			this.parameters.push({
				DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButton:
					'DesktopModuleToolbarSegmentedButtonStandardFilterSelectionButtonCustom',
			});
		}

		this.parameters.push({ DesktopModuleCustomFilterCondition: {} });

		this.parameters.push({
			DesktopModuleToolbarCycleButtonDirection: 'DesktopModuleToolbarCycleButtonDirectionBidirectional',
		});

		this.parameters.push({ DesktopModuleBoxIds: boxIdFilter });

		this.parameters.push({
			DesktopModuleToolbarSplitButtonUserFilter: userIdFilter,
		});
	}

	// Instanz-scoped Subscription-Opt-out (Task 3.1e). Die DEFAULT-Liste (`taskList`) bleibt bei aktiver Suche
	// bewusst live-abonniert — das ist das klassische, absichtliche Verhalten aus Commit a1b8968ac
	// ("Keep multi-lane searches consistent across tabs and stale reloads") für die Kanban-Multi-Lane-Suche.
	// Nur ISOLIERTE Instanz-Stores (gespeicherte-Listen-Tabs, `$id !== 'taskList'`) rufen diese Methode auf:
	// dort würde der Subscription-Replay auf dem geteilten Socket die view-scoped HTTP-Antworten quer über
	// alle drei Ansichts-Slices überschreiben (Zähler-Bug 8/8/8 statt 3/0/0). Bei leerer Suche No-op.
	disableSubscriptionsForInstanceSearch(searchTerm: string, hiddenSearchTerm: string): void {
		this.disableSubscriptionsWhileSearchIsActive(searchTerm, hiddenSearchTerm);
	}

	uniqueQueryIdentifier(): string {
		return this.createStableIdentifier({
			standardFilterSelection: this.standardFilterSelection,
		});
	}
}
